import { useCallback } from 'react'

import { locationHasError } from '@/server-data'

import * as OrderTexts from '../../../../../../intl'
import { form } from '../../../formState'
import type { Props } from '../typings'

export function Address(props: Props) {
  const { orderStepType } = props

  const type = form.useFormField('type').field.value
  const formApi = form.useFormApi()

  // orderStepType === 'service' become 'delivery'
  const name = orderStepType === 'pickup' ? 'pickup.location' : 'delivery.location'

  // depotId lives alongside the location, under the same pickup/delivery key
  const depotIdName = orderStepType === 'pickup' ? 'pickup.depotId' : 'delivery.depotId'

  // Read the depotId from its own leaf field, NOT from `orderStep.depotId`.
  // `orderStep` is the parent `pickup`/`delivery` field value, which stays stale
  // when we write the leaf via `formApi.change(depotIdName, ...)`. Feeding a stale
  // connectedPlaceId back into the LocationEditor breaks its "did the connected
  // place change?" guard: re-selecting the originally-connected depot after
  // switching to another one is skipped, so the order saves to the wrong depot.
  const depotId = form.useFormField(depotIdName).field.value as string | undefined

  const handleConnectedPlaceChanged = useCallback(
    (connectedId?: string) => {
      formApi.change(depotIdName, connectedId)
    },
    [formApi, depotIdName],
  )

  const pinId: uui.domain.ui.map.LocationPinId =
    type === 'p'
      ? 'orderForm_pickup'
      : type === 's'
        ? 'orderForm_service'
        : type === 'pd'
          ? orderStepType === 'pickup'
            ? 'orderForm_pickup'
            : 'orderForm_delivery'
          : 'orderForm_delivery'

  form.useAddFieldValidation<typeof name>(
    name,
    (field, _formApi) => {
      const location = field.value

      return !location || locationHasError(location)
    },
    { message: '' },
  )

  return (
    <form.LocationEditorField
      name={name}
      pinType="normal"
      snapToRoad={true}
      pinId={pinId}
      ignoreMapBounds={false}
      label={OrderTexts.getFieldLabelAddressText()}
      testId="address"
      showAdjustAddress
      disabled={false}
      connectedPlaceId={depotId}
      assetTypes={['depot']}
      onConnectedPlaceChanged={handleConnectedPlaceChanged}
    />
  )
}
